import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Alert,
  Slider,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayArrow as StartIcon,
  HelpOutline as HelpIcon,
  TuneOutlined as FilterIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL;

const areaNames = {
  matematicas: 'Matemáticas',
  'lectura-critica': 'Lectura Crítica',
  sociales: 'Sociales y Ciudadanas',
  ciencias: 'Ciencias Naturales',
  ingles: 'Inglés',
};

const topicNames = {
  algebra: 'Álgebra',
  geometria: 'Geometría',
  estadistica: 'Estadística',
  'comprension-lectora': 'Comprensión Lectora',
  'tipos-de-texto': 'Tipos de Texto',
  'literatura-latinoamericana': 'Literatura Latinoamericana',
  'historia-colombia': 'Historia de Colombia',
  'geografia-colombia': 'Geografía de Colombia',
  'constitucion-politica': 'Constitución Política',
  biologia: 'Biología',
  quimica: 'Química',
  fisica: 'Física',
  'reading-comprehension': 'Reading Comprehension',
  grammar: 'Grammar',
  vocabulary: 'Vocabulary',
};

const ExamConfig = () => {
  const { area } = useParams();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [allTopics, setAllTopics] = useState(true);
  const [numQuestions, setNumQuestions] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Nuevos estados para filtros adicionales
  const [structure, setStructure] = useState(null);
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    fetchTopics();
    fetchStructure();
  }, [area]);

  const fetchStructure = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/structure`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Structure loaded:', response.data);
      console.log('Area:', area);
      console.log('Competencies for area:', response.data?.areas?.[area]?.competencies);
      setStructure(response.data);
    } catch (err) {
      console.error('Error fetching structure:', err);
    }
  };

  const fetchTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/exams/topics/${area}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Topics loaded:', response.data);
      setTopics(response.data);
      setSelectedTopics(response.data); // Por defecto, todos seleccionados
      setLoading(false);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setError('Error al cargar los temas');
      setLoading(false);
    }
  };

  const handleTopicChange = (topic) => {
    setAllTopics(false);
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter((t) => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const handleAllTopicsChange = (event) => {
    setAllTopics(event.target.checked);
    if (event.target.checked) {
      setSelectedTopics(topics);
    }
  };

  const handleStartExam = () => {
    const topicsQuery = allTopics ? '' : `&topics=${selectedTopics.join(',')}`;
    const competenciesQuery = selectedCompetencies.length > 0 ? `&competencies=${selectedCompetencies.join(',')}` : '';
    const difficultyQuery = selectedDifficulty !== 'all' ? `&difficulty=${selectedDifficulty}` : '';
    
    navigate(`/exam/${area}?limit=${numQuestions}${topicsQuery}${competenciesQuery}${difficultyQuery}`);
  };

  const handleBack = () => {
    navigate(`/area/${area}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<BackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Volver
      </Button>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          ⚙️ Configurar Examen
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {areaNames[area]}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Número de preguntas
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={numQuestions}
              onChange={(e, value) => setNumQuestions(value)}
              min={5}
              max={50}
              step={5}
              marks
              valueLabelDisplay="on"
              sx={{ mb: 2 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Selecciona cuántas preguntas quieres responder (5-50)
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend">
              <Typography variant="h6">Temas a incluir</Typography>
            </FormLabel>

            <FormControlLabel
              control={
                <Checkbox
                  checked={allTopics}
                  onChange={handleAllTopicsChange}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" fontWeight="bold">
                  Todos los temas
                </Typography>
              }
              sx={{ mb: 2, mt: 1 }}
            />

            {!allTopics && (
              <FormGroup>
                {topics.map((topic) => (
                  <FormControlLabel
                    key={topic}
                    control={
                      <Checkbox
                        checked={selectedTopics.includes(topic)}
                        onChange={() => handleTopicChange(topic)}
                        color="primary"
                      />
                    }
                    label={topicNames[topic] || topic}
                    sx={{ ml: 2 }}
                  />
                ))}
              </FormGroup>
            )}
          </FormControl>
        </Box>

        {!allTopics && selectedTopics.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Temas seleccionados:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedTopics.map((topic) => (
                <Chip
                  key={topic}
                  label={topicNames[topic] || topic}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Filtros Avanzados */}
        <Box sx={{ mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            fullWidth
            startIcon={<FilterIcon />}
            sx={{ mb: 2 }}
          >
            {showAdvancedFilters ? '▼' : '▶'} Filtros Avanzados (Opcional)
          </Button>

          {showAdvancedFilters && (
            <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              {/* Dificultad */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Dificultad
                  </Typography>
                  <Tooltip title="Filtra preguntas según su nivel de complejidad">
                    <IconButton size="small">
                      <HelpIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <FormControl fullWidth>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label="Todas"
                      color={selectedDifficulty === 'all' ? 'primary' : 'default'}
                      onClick={() => setSelectedDifficulty('all')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label="Fácil"
                      color={selectedDifficulty === 'easy' ? 'success' : 'default'}
                      onClick={() => setSelectedDifficulty('easy')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label="Medio"
                      color={selectedDifficulty === 'medium' ? 'warning' : 'default'}
                      onClick={() => setSelectedDifficulty('medium')}
                      sx={{ cursor: 'pointer' }}
                    />
                    <Chip
                      label="Difícil"
                      color={selectedDifficulty === 'hard' ? 'error' : 'default'}
                      onClick={() => setSelectedDifficulty('hard')}
                      sx={{ cursor: 'pointer' }}
                    />
                  </Box>
                </FormControl>
              </Box>

              {/* Competencias */}
              {structure && structure.areas && structure.areas[area] && structure.areas[area].competencies && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Competencias
                    </Typography>
                    <Tooltip title="Filtra preguntas según las competencias específicas que se evalúan">
                      <IconButton size="small">
                        <HelpIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <FormGroup>
                    {structure.areas[area].competencies.map((comp) => (
                      <FormControlLabel
                        key={comp.id}
                        control={
                          <Checkbox
                            checked={selectedCompetencies.includes(comp.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCompetencies([...selectedCompetencies, comp.id]);
                              } else {
                                setSelectedCompetencies(selectedCompetencies.filter(c => c !== comp.id));
                              }
                            }}
                            color="primary"
                          />
                        }
                        label={comp.name}
                      />
                    ))}
                  </FormGroup>
                </Box>
              )}

              {selectedCompetencies.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Competencias seleccionadas:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedCompetencies.map((compId) => {
                      const comp = structure?.areas?.[area]?.competencies?.find(c => c.id === compId);
                      return (
                        <Chip
                          key={compId}
                          label={comp?.name || compId}
                          color="secondary"
                          variant="outlined"
                          onDelete={() => setSelectedCompetencies(selectedCompetencies.filter(c => c !== compId))}
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<StartIcon />}
            onClick={handleStartExam}
            disabled={!allTopics && selectedTopics.length === 0}
            sx={{ px: 6, py: 1.5 }}
          >
            Iniciar Examen
          </Button>
        </Box>

        {!allTopics && selectedTopics.length === 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            Debes seleccionar al menos un tema o marcar "Todos los temas"
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default ExamConfig;
